import { Alert, Button, CircularProgress, Dialog,DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid } from "@mui/system";
import { useEffect, useState ,useRef} from "react";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import prevIcon from '../../assets/icons/left-arrow.svg';
import nextIcon from '../../assets/icons/right-arrow.svg';
import config from "../../config/config";
import { makeGetRequestToUrl, makePostRequestToUrl } from "../commonFunc";
import { useNavigate,useSearchParams } from "react-router-dom";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import constant from "../../constant";

// Card brand -> tile colour, keeps the same tint family as the dashboard stat cards
const brandTint = (brand = '') => {
    const b = brand.toLowerCase();
    if (b.includes('visa')) return { bg: '#F3F8FF', accent: '#0065FF' };
    if (b.includes('master')) return { bg: '#FFFBF0', accent: '#B07D18' };
    if (b.includes('verve')) return { bg: '#EFFBF7', accent: '#0DBA78' };
    return { bg: '#F5F6F8', accent: '#1A1E25' };
};

// Normalise whatever shape the /paystack/corporate/card endpoint returns
const normaliseCard = (c, i) => ({
    id: c.id ?? c.card_id ?? c.authorization_code ?? i,
    brand: c.brand ?? c.card_type ?? c.cardType ?? 'Card',
    last4: c.last4 ?? c.last_4 ?? c.last4Digits ?? '••••',
    bin: c.bin ?? c.first6 ?? '',
    expMonth: c.exp_month ?? c.expMonth ?? '',
    expYear: c.exp_year ?? c.expYear ?? '',
    bank: c.bank ?? c.bank_name ?? '',
    holder: c.account_name ?? c.card_holder ?? c.holder_name ?? '',
    isDefault: Boolean(c.is_default ?? c.isDefault ?? c.default),
    status: c.status ?? (c.reusable === false ? 'inactive' : 'active'),
    addedOn: c.created_at ?? c.createdAt ?? c.added_on ?? '',
    raw: c,
});

const ManageCards = () => {
    const serverUrl = config.serverUrl;
    const navigate = useNavigate();

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tokenExpired, setTokenExpired] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [paginatedData, setPaginatedData] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [deleteTarget, setDeleteTarget] = useState(null); // card being deleted
    const [deleting, setDeleting] = useState(false);
    const [cardModal, setCardModal] = useState({ open: false, url: '', reference: '', result: null });
    const iframeLoads = useRef(0);
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null });
    const [pdfBlobUrl, setPdfBlobUrl] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(false);
    const client_id = localStorage.getItem('clientId');
    const paymentType_client = constant.payment_client_id[client_id];
    const NO_IFRAME_MODES = ['stripe'];

    const [downloadingInvoice, setDownloadingInvoice] = useState(false);
    useEffect(() => {
        setPaginatedData(cards.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
    }, [cards, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleSessionExpired = () => {
        localStorage.removeItem('token');
        setErrorMessage('Your session has expired. Please log in again.');
        setTokenExpired(true);
        setTimeout(() => { navigate('/signup'); }, 2000);
    };

    // ---- Fetch saved cards --------------------------------------------------
    const fetchSavedCards = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const timestamp = new Date().getTime();
            const url = `${serverUrl}/payment/corporate/card`;

            const response = await makePostRequestToUrl(url, { token,payment_mode:paymentType_client});

            if (response.flag == 200) {
                const d = response.data;
                const list = Array.isArray(d) ? d
                    : Array.isArray(d?.cards) ? d.cards
                    : Array.isArray(d?.data) ? d.data
                    : d && typeof d === 'object' && Object.keys(d).length ? [d]
                    : [];
                setCards(list.map(normaliseCard));
                setPage(0);
            } else if (response.flag == 102) {
                handleSessionExpired();
            } else {
                throw new Error(response.message || `HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error fetching cards:', error);
            setToast({ open: true, message: 'Could not load saved cards. Try refreshing.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };
        // Handle return from Paystack: ?card=success | ?card=failed
    useEffect(() => {
        const result = searchParams.get('card');
        if (!result) return;

        if (result === 'success') {
            setToast({ open: true, message: 'Card added successfully.', severity: 'success' });
            fetchSavedCards();
        } else if (result === 'failed') {
            setToast({ open: true, message: 'Card could not be added. Please try again.', severity: 'error' });
        }

        // Clear the param so the toast doesn't reappear on refresh
        searchParams.delete('card');
        setSearchParams(searchParams, { replace: true });
    }, []);

    useEffect(() => {
        fetchSavedCards();
    }, []);

    // ---- Add card (initiate) ------------------------------------------------
    // const handleAddCards = async () => {
    //     try {
    //         setAdding(true);
    //         const token = localStorage.getItem('token');
    //         const url = `${serverUrl}/payment/corporate/add_card/initiate`;
    //         const body = {
    //             token,
    //             payment_mode:paymentType_client
    //         }

    //         const response = await makePostRequestToUrl(url, { token ,payment_mode:paymentType_client});

    //         if (response.flag == 102) {
    //             handleSessionExpired();
    //             return;
    //         }

    //         const redirectUrl = response.data?.authorization_url
    //             ?? response.data?.url
    //             ?? response.authorization_url
    //             ?? response.url;

    //         if (!redirectUrl) {
    //             throw new Error(response.message || 'No authorization URL returned');
    //         }

    //         const reference = response.data?.reference ?? response.reference ?? '';
    //         if (NO_IFRAME_MODES.includes(String(paymentType_client).toLowerCase())) {
    //             // Top-level redirect: Stripe will send the user back to success_url / cancel_url
    //             window.location.href = redirectUrl;
    //             return;
    //         }
    //         iframeLoads.current = 0;
    //         setCardModal({ open: true, url: redirectUrl, reference, result: null });
    //     } catch (error) {
    //         console.error('Error initiating add card:', error);
    //         setToast({ open: true, message: 'Could not start card setup. Please try again.', severity: 'error' });
    //     } finally {
    //         setAdding(false);
    //     }
    // };

    // Gateways whose hosted page allows being embedded
const canEmbed = (url) => {
    try {
        const host = new URL(url).hostname;
        return host.endsWith('paystack.com') || host.endsWith('paystack.co');
    } catch {
        return false;
    }
};

useEffect(() => {
    const result = searchParams.get('card');
    const sessionId = searchParams.get('session_id');
    if (!result) return;

    (async () => {
        if (result === 'success') {
            if (sessionId) {
                try {
                    const token = localStorage.getItem('token');
                    await makePostRequestToUrl(`${serverUrl}/payment/corporate/add_card/status`, {
                        token, reference: sessionId, payment_mode: paymentType_client,
                    });
                } catch (e) {
                    console.error('Status check failed', e);
                }
            }
            setToast({ open: true, message: 'Card added successfully.', severity: 'success' });
            fetchSavedCards();
        } else if (result === 'failed') {
            setToast({ open: true, message: 'Card could not be added. Please try again.', severity: 'error' });
        }

        searchParams.delete('card');
        searchParams.delete('session_id');
        setSearchParams(searchParams, { replace: true });
    })();
}, []);
const handleAddCards = async () => {
    // Must happen directly in the click handler, before any await
    const newTab = window.open('', '_blank');

    try {
        setAdding(true);
        const token = localStorage.getItem('token');
        const url = `${serverUrl}/payment/corporate/add_card/initiate`;

        const response = await makePostRequestToUrl(url, { token, payment_mode: paymentType_client });

        if (response.flag == 102) {
            newTab?.close();
            handleSessionExpired();
            return;
        }

        const redirectUrl = response.data?.authorization_url
            ?? response.data?.url
            ?? response.authorization_url
            ?? response.url;

        if (!redirectUrl) {
            throw new Error(response.message || 'No authorization URL returned');
        }

        const reference = response.data?.reference ?? response.reference ?? '';

        if (!canEmbed(redirectUrl)) {
            // Stripe: open in the new tab
            if (newTab) {
                newTab.location.href = redirectUrl;

                // When the user finishes and the tab closes, refresh the list here
                const timer = setInterval(() => {
                    if (newTab.closed) {
                        clearInterval(timer);
                        fetchSavedCards();
                    }
                }, 1000);
            } else {
                // Popup was blocked, fall back to the same tab
                window.location.href = redirectUrl;
            }
            return;
        }

        // Paystack: keep using the iframe dialog, no new tab needed
        newTab?.close();
        iframeLoads.current = 0;
        setCardModal({ open: true, url: redirectUrl, reference, result: null });
    } catch (error) {
        newTab?.close();
        console.error('Error initiating add card:', error);
        setToast({ open: true, message: 'Could not start card setup. Please try again.', severity: 'error' });
    } finally {
        setAdding(false);
    }
};

    const fetchInvoices = async () => {
    try {
        setInvoicesLoading(true);
        const token = localStorage.getItem('token');
        const url = `${serverUrl}/corporate/invoices`;
        const response = await makeGetRequestToUrl(url, { token });

        if (response.flag == 102) { handleSessionExpired(); return; }

        // Endpoint returns { message, data } with no flag
        if (response.flag == 200 || Array.isArray(response.data)) {
            setInvoices(response.data || []);
        } else {
            throw new Error(response.message || 'Failed to fetch invoices');
        }
    } catch (error) {
        console.error('Error fetching invoices:', error);
        setToast({ open: true, message: 'Could not load invoices.', severity: 'error' });
    } finally {
        setInvoicesLoading(false);
    }
};

useEffect(() => { fetchInvoices(); }, []);

const openInvoiceModal = (invoice) => setInvoiceModal({ open: true, invoice });
const closeInvoiceModal = () => { setInvoiceModal({ open: false, invoice: null }); setPdfBlobUrl(''); };

// Load the PDF as a blob so it renders regardless of S3's content-type
useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    const inv = invoiceModal.invoice;
    if (!invoiceModal.open || !inv?.invoice_url) { setPdfBlobUrl(''); return; }

    (async () => {
        setPdfLoading(true);
        setPdfError(false);
        try {
            const res = await fetch(inv.invoice_url);
            if (!res.ok) throw new Error('fetch failed');
            const raw = await res.blob();
            objectUrl = URL.createObjectURL(new Blob([raw], { type: 'application/pdf' }));
            if (!cancelled) setPdfBlobUrl(objectUrl);
        } catch (e) {
            console.error('PDF load failed', e);
            if (!cancelled) { setPdfBlobUrl(''); setPdfError(true); }
        } finally {
            if (!cancelled) setPdfLoading(false);
        }
    })();

    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
}, [invoiceModal]);

const handleDownloadInvoice = async (invoice) => {
    if (!invoice?.invoice_url) return;
    setDownloadingInvoice(true);
    try {
        const res = await fetch(invoice.invoice_url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${invoice.invoice_number || 'invoice'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    } catch (err) {
        window.open(invoice.invoice_url, '_blank', 'noopener');
    } finally {
        setDownloadingInvoice(false);
    }
};

const invoiceStatus = (s) => (s == 1 ? { label: 'Pending', bg: '#E7F8F2', color: '#B07D18' }
    : s == 2 ? { label: 'Paid', bg: '#FDECEC', color: '#0DBA78' }
    : { label: 'Failed', bg: '#FBEFD2', color: '#D14545' });

    // Modal closed → refetch and see whether a card was added
    // Modal closed → ask the backend whether the card was added
    // Called each time the iframe navigates; asks the backend whether the reference has settled
    const checkCardStatus = async () => {
        const reference = cardModal.reference;
        if (!reference || cardModal.result) return;

        try {
            const token = localStorage.getItem('token');
            const url = `${serverUrl}/payment/corporate/add_card/status`;
            const response = await makePostRequestToUrl(url, { token, reference,payment_mode:paymentType_client });

            if (response.flag == 102) {
                handleSessionExpired();
                return;
            }

            const status = String(response.data?.status ?? '').toLowerCase();
            if (status === 'success' || status === 'successful') {
                setCardModal((m) => ({ ...m, result: 'success' }));
            } else if (status === 'failed' || status === 'failure') {
                setCardModal((m) => ({ ...m, result: 'failed' }));
            }
            // anything else = still pending, keep the iframe up
        } catch (error) {
            console.error('Error checking card status:', error);
        }
    };

    // Modal closed → refresh list and toast based on the result we already have
    const handleCardModalClose = async () => {
        const result = cardModal.result;
        setCardModal({ open: false, url: '', reference: '', result: null });

        if (result === 'success') {
            setToast({ open: true, message: 'Card added successfully.', severity: 'success' });
        } else if (result === 'failed') {
            setToast({ open: true, message: 'Card could not be added. Please try again.', severity: 'error' });
        }
        await fetchSavedCards();
    };
        // ---- Delete card ------------------------------------------------------
    const handleDeleteCard = async () => {
        try {
            setDeleting(true);
            const token = localStorage.getItem('token');
            const url = `${serverUrl}/payment/corporate/delete_card`;

            const response = await makePostRequestToUrl(url, { token,payment_mode:paymentType_client });

            if (response.flag == 200) {
                setToast({ open: true, message: response.message, severity: 'success' });
                setDeleteTarget(null);
                await fetchSavedCards();
            } 
            else if(response.flag == 410){
                setToast({ open: true, message: response.message, severity: 'error' });
            }
            else if (response.flag == 102) {
                handleSessionExpired();
            } else {
                throw new Error(response.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting card:', error);
            setToast({ open: true, message: error, severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    // Refresh when the user returns to this tab (e.g. after finishing on Paystack)
    // useEffect(() => {
    //     const onFocus = () => fetchSavedCards();
    //     window.addEventListener('focus', onFocus);
    //     return () => window.removeEventListener('focus', onFocus);
    // }, []);

    const totalPages = Math.ceil(cards.length / rowsPerPage);
    const defaultCard = cards.find((c) => c.isDefault);
    const activeCount = cards.filter((c) => c.status === 'active').length;

    return (
        <div>
            <Grid container sx={{ alignItems: 'center' }}>
                <Typography style={{ fontSize: '28px', fontWeight: '500' }}>Manage Cards</Typography>
                <Grid item sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={fetchSavedCards}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{
                            textTransform: 'none', height: 40, borderRadius: '8px', backgroundColor: '#FFFFFF',
                            color: '#1A1E25', borderColor: '#E4E7EC',
                            '&:hover': { borderColor: '#0065FF', backgroundColor: '#FFFFFF' },
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddCards}
                        disabled={adding || tokenExpired}
                        startIcon={adding ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AddIcon />}
                        sx={{
                            textTransform: 'none', height: 40, borderRadius: '8px', backgroundColor: '#0065FF',
                            boxShadow: 'none', px: 2.5,
                            '&:hover': { backgroundColor: '#0052CC', boxShadow: 'none' },
                        }}
                    >
                        Add card
                    </Button>
                </Grid>
            </Grid>

            {errorMessage && (
                <Alert severity="warning" sx={{ marginTop: '1%', borderRadius: '8px' }}>{errorMessage}</Alert>
            )}

            {/* Summary tiles, same tint family as the dashboard */}
            <Grid className="mainBox" sx={{ marginTop: '1%' }}>
                <Grid container sx={{ marginTop: '1%', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                    <Grid item sx={{ backgroundColor: '#F3F8FF', padding: '4%', borderRadius: '12px', minHeight: '130px' }}>
                        <CreditCardIcon sx={{ fontSize: 32, color: '#0065FF' }} />
                        <Typography sx={{ fontSize: '16px', color: '#1A1E25' }}>Saved cards</Typography>
                        <Typography sx={{ fontSize: '24px', color: '#1A1E25', fontWeight: '600', marginTop: '4px' }}>
                            {cards.length}
                        </Typography>
                    </Grid>
                    <Grid item sx={{ backgroundColor: '#EFFBF7', padding: '4%', borderRadius: '12px', minHeight: '130px' }}>
                        <FiberManualRecordIcon sx={{ fontSize: 32, color: '#0DBA78' }} />
                        <Typography sx={{ fontSize: '16px', color: '#1A1E25' }}>Active cards</Typography>
                        <Typography sx={{ fontSize: '24px', color: '#1A1E25', fontWeight: '600', marginTop: '4px' }}>
                            {activeCount}
                        </Typography>
                    </Grid>
                    <Grid item sx={{ backgroundColor: '#FFFBF0', padding: '4%', borderRadius: '12px', minHeight: '130px' }}>
                        <CreditCardIcon sx={{ fontSize: 32, color: '#B07D18' }} />
                        <Typography sx={{ fontSize: '16px', color: '#1A1E25' }}>Default card</Typography>
                        <Typography sx={{ fontSize: '24px', color: '#1A1E25', fontWeight: '600', marginTop: '4px' }}>
                            {defaultCard ? `${defaultCard.brand} •••• ${defaultCard.last4}` : '—'}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {/* Card tiles */}
            <Grid className="mainBox" sx={{ marginTop: '1%' }}>
                <Typography sx={{ fontSize: '24px', color: '#1A1E25', paddingTop: '1%' }}>Your cards</Typography>

                {!loading && cards.length === 0 && (
                    <Box sx={{
                        marginTop: '16px', padding: '40px', borderRadius: '12px', border: '1px dashed #E4E7EC',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center',
                    }}>
                        <CreditCardIcon sx={{ fontSize: 40, color: '#C4C8CE' }} />
                        <Typography sx={{ fontSize: '16px', color: '#1A1E25', fontWeight: 500 }}>No cards saved yet</Typography>
                        <Typography sx={{ fontSize: '14px', color: '#83888E', maxWidth: 360 }}>
                            Add a card to pay for corporate trips. You'll be taken to a secure Paystack page to enter the details.
                        </Typography>
                        {/* <Button
                            variant="contained" onClick={handleAddCard} disabled={adding} startIcon={<AddIcon />}
                            sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#0065FF', boxShadow: 'none', mt: 1,
                                '&:hover': { backgroundColor: '#0052CC', boxShadow: 'none' } }}
                        >
                            Add card
                        </Button> */}
                    </Box>
                )}

                {cards.length > 0 && (
                    <Grid container sx={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                        {cards.map((card) => {
                            const tint = brandTint(card.brand);
                            return (
                                <Box key={card.id} sx={{
                                    backgroundColor: tint.bg, borderRadius: '12px', padding: '20px', minHeight: '160px',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                    border: card.isDefault ? `1px solid ${tint.accent}` : '1px solid transparent',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tint.accent, textTransform: 'capitalize' }}>
                                            {card.brand}
                                        </Typography>
                                        {card.isDefault && (
                                            <Box sx={{ fontSize: '12px', color: tint.accent, backgroundColor: '#FFFFFF', borderRadius: '6px', px: 1, py: 0.25 }}>
                                                Default
                                            </Box>
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1A1E25', letterSpacing: '2px', mt: 2 }}>
                                        {card.bin ? `${card.bin.slice(0, 4)} ••••` : '•••• ••••'} •••• {card.last4}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 2 }}>
                                        <Box>
                                            {card.holder && <Typography sx={{ fontSize: '13px', color: '#1A1E25' }}>{card.holder}</Typography>}
                                            {card.bank && <Typography sx={{ fontSize: '12px', color: '#83888E' }}>{card.bank}</Typography>}
                                        </Box>
                                        <Typography sx={{ fontSize: '12px', color: '#83888E' }}>
                                            {card.expMonth && card.expYear ? `Exp ${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Grid>
                )}
            </Grid>

            {/* Table view, same classes as Staff Spend Report */}
            <Grid className="mainBox" sx={{ marginTop: '1%' }}>
                <Grid container>
                    <Typography sx={{ fontSize: '24px', color: '#1A1E25', paddingTop: '1%' }}>Card details</Typography>

                    <TableContainer className="table-container" sx={{ maxHeight: '56vh', marginTop: '12px' }}>
                        <Table stickyHeader className="scrollable-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" className="table-header" style={{ width: '18%' }}>Card</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '16%' }}>Number</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '12%' }}>Expiry</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '18%' }}>Bank</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '18%' }}>Added on</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '18%' }}>Status</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '18%' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.length == 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body1">{loading ? 'Loading cards…' : 'No data found'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {paginatedData.map((card) => {
                                    const tint = brandTint(card.brand);
                                    return (
                                        <TableRow key={card.id}>
                                            <TableCell>
                                                <Grid container sx={{ alignItems: 'center', gap: '12px', minWidth: '160px' }}>
                                                    <Box sx={{ width: 40, height: 28, borderRadius: '6px', backgroundColor: tint.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <CreditCardIcon sx={{ fontSize: 18, color: tint.accent }} />
                                                    </Box>
                                                    <Box sx={{ textAlign: 'left' }}>
                                                        <Typography sx={{ fontSize: '14px', textTransform: 'capitalize' }}>{card.brand}</Typography>
                                                        {card.isDefault && <Typography sx={{ fontSize: '12px', color: '#0065ff' }}>Default</Typography>}
                                                    </Box>
                                                </Grid>
                                            </TableCell>
                                            <TableCell align="center">•••• {card.last4}</TableCell>
                                            <TableCell align="center">
                                                {card.expMonth && card.expYear ? `${String(card.expMonth).padStart(2, '0')}/${card.expYear}` : '—'}
                                            </TableCell>
                                            <TableCell align="center">{card.bank || '—'}</TableCell>
                                            <TableCell align="center">
                                                {card.addedOn ? new Date(card.addedOn).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="contained"
                                                    style={{
                                                        backgroundColor: card.status == 'active' ? '#E7F8F2' : '#FBEFD2',
                                                        color: card.status == 'active' ? '#0DBA78' : '#B07D18',
                                                        textTransform: 'none', pointerEvents: 'none', borderRadius: '6px',
                                                        minWidth: '40px', border: 'none', boxShadow: 'none',
                                                    }}
                                                >
                                                    <FiberManualRecordIcon style={{ fontSize: '12px', marginRight: '8px' }} />{card.status}
                                                </Button>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteTarget(card)}
                                                    sx={{ color: '#83888E', '&:hover': { color: '#D14545', backgroundColor: '#FDECEC' } }}
                                                    aria-label="Remove card"
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {cards.length > 0 && (
                            <div className="custom-pagination">
                                <div className="pagination-info">
                                    {`Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, cards.length)} of ${cards.length} entries`}
                                </div>
                                <div className="pagination-controls">
                                    <button onClick={() => handleChangePage(null, page - 1)} disabled={page === 0} className="pagination-arrow">
                                        <img src={prevIcon} alt="Previous" />
                                    </button>
                                    <div className="pagination-numbers">
                                        {page > 1 && (
                                            <>
                                                <button onClick={() => handleChangePage(null, 0)} className="pagination-number">1</button>
                                                <span className="pagination-dots">...</span>
                                            </>
                                        )}
                                        {[...Array(totalPages)]
                                            .map((_, index) => index)
                                            .filter((index) => index >= page - 1 && index <= page + 1)
                                            .map((index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleChangePage(null, index)}
                                                    className={`pagination-number ${page === index ? 'active' : ''}`}
                                                >
                                                    {index + 1}
                                                </button>
                                            ))}
                                        {page < totalPages - 2 && (
                                            <>
                                                <span className="pagination-dots">...</span>
                                                <button onClick={() => handleChangePage(null, totalPages - 1)} className="pagination-number">
                                                    {totalPages}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <button onClick={() => handleChangePage(null, page + 1)} disabled={page >= totalPages - 1} className="pagination-arrow">
                                        <img src={nextIcon} alt="Next" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </TableContainer>
                </Grid>
            </Grid>
            <Grid className="mainBox" sx={{ marginTop: '1%' }}>
    <Grid container>
        <Typography sx={{ fontSize: '24px', color: '#1A1E25', paddingTop: '1%' }}>Invoices</Typography>

        <TableContainer className="table-container" sx={{ maxHeight: '56vh', marginTop: '12px' }}>
            <Table stickyHeader className="scrollable-table">
                <TableHead>
                    <TableRow>
                        <TableCell align="center" className="table-header">Invoice No.</TableCell>
                        <TableCell align="center" className="table-header">Billing Month</TableCell>
                        <TableCell align="center" className="table-header">Total Rides</TableCell>
                        <TableCell align="center" className="table-header">Amount</TableCell>
                        <TableCell align="center" className="table-header">Created On</TableCell>
                        <TableCell align="center" className="table-header">Status</TableCell>
                        <TableCell align="center" className="table-header">Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {invoices.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} align="center">
                                <Typography variant="body1">{invoicesLoading ? 'Loading invoices…' : 'No invoices found'}</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {invoices.map((inv) => {
                        const st = invoiceStatus(inv.status);
                        return (
                            <TableRow key={inv.id}>
                                <TableCell align="center">{inv.invoice_number || '—'}</TableCell>
                                <TableCell align="center">{inv.billing_month || '—'}</TableCell>
                                <TableCell align="center">{inv.total_rides ?? 0}</TableCell>
                                <TableCell align="center">{Number(inv.total_amount || 0).toFixed(2)}</TableCell>
                                <TableCell align="center">
                                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </TableCell>
                                <TableCell align="center">
                                    <Button variant="contained" style={{
                                        backgroundColor: st.bg, color: st.color, textTransform: 'none', pointerEvents: 'none',
                                        borderRadius: '6px', minWidth: '40px', border: 'none', boxShadow: 'none',
                                    }}>
                                        <FiberManualRecordIcon style={{ fontSize: '12px', marginRight: '8px' }} />{st.label}
                                    </Button>
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        onClick={() => openInvoiceModal(inv)}
                                        sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid #0065FF', color: '#0065FF', px: 2, height: 34,
                                            '&:hover': { backgroundColor: '#0065FF', color: '#fff' } }}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </Grid>
</Grid>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ borderRadius: '8px' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
                        <Dialog
                open={cardModal.open}
                onClose={handleCardModalClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px', height: '80vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>Add card</Typography>
                    <IconButton onClick={handleCardModalClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {cardModal.result ? (
                        <Box sx={{
                            height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: 1.5, textAlign: 'center', px: 4,
                        }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: cardModal.result === 'success' ? '#EFFBF7' : '#FDECEC',
                            }}>
                                <CreditCardIcon sx={{ fontSize: 32, color: cardModal.result === 'success' ? '#0DBA78' : '#D14545' }} />
                            </Box>
                            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1A1E25' }}>
                                {cardModal.result === 'success' ? 'Card added' : 'Card not added'}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#83888E', maxWidth: 320 }}>
                                {cardModal.result === 'success'
                                    ? 'Please close this window to see your updated card list.'
                                    : 'The card could not be saved. Please close this window and try again.'}
                            </Typography>
                            <Button
                                variant="contained" onClick={handleCardModalClose}
                                sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#0065FF', boxShadow: 'none', mt: 1,
                                    '&:hover': { backgroundColor: '#0052CC', boxShadow: 'none' } }}
                            >
                                Close
                            </Button>
                        </Box>
                    ) : (
                        cardModal.url && (
                            <iframe
                                src={cardModal.url}
                                title="Add card"
                                onLoad={() => {
                                    iframeLoads.current += 1;
                                    if (iframeLoads.current > 1) checkCardStatus();
                                }}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                allow="payment"
                            />
                        )
                    )}
                </DialogContent>
            </Dialog>
                        <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontSize: '18px', fontWeight: 500 }}>Remove card?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px', color: '#83888E' }}>
                        {deleteTarget && (
                            <>The <span style={{ textTransform: 'capitalize' }}>{deleteTarget.brand}</span> card ending in {deleteTarget.last4} will no longer be used for corporate trips.</>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={deleting}
                        sx={{ textTransform: 'none', color: '#1A1E25' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteCard}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteOutlineIcon />}
                        sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#D14545', boxShadow: 'none',
                            '&:hover': { backgroundColor: '#B83A3A', boxShadow: 'none' } }}
                    >
                        Remove card
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={invoiceModal.open} onClose={closeInvoiceModal} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', height: '88vh' } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{invoiceModal.invoice?.invoice_number}</Typography>
                        <Typography sx={{ fontSize: '13px', color: '#83888E' }}>
                            {invoiceModal.invoice?.billing_month} · {invoiceModal.invoice?.total_rides ?? 0} rides · {Number(invoiceModal.invoice?.total_amount || 0).toFixed(2)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            onClick={() => handleDownloadInvoice(invoiceModal.invoice)}
                            disabled={downloadingInvoice}
                            sx={{ textTransform: 'none', borderRadius: '8px', height: 38, px: 2.5, backgroundColor: '#0065FF', color: '#fff',
                                '&:hover': { backgroundColor: '#0052CC' }, '&.Mui-disabled': { backgroundColor: '#E5E7EB', color: '#9CA3AF' } }}
                        >
                            {downloadingInvoice ? 'Downloading…' : 'Download PDF'}
                        </Button>
                        <IconButton onClick={closeInvoiceModal} size="small"><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, backgroundColor: '#F3F4F6' }}>
                    {pdfLoading ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : pdfBlobUrl ? (
                        <iframe src={`${pdfBlobUrl}#toolbar=0`} title={invoiceModal.invoice?.invoice_number}
                            style={{ width: '100%', height: '100%', border: 'none' }} />
                    ) : pdfError ? (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: '#83888E' }}>Preview unavailable.</Typography>
                            <Button onClick={() => window.open(invoiceModal.invoice?.invoice_url, '_blank', 'noopener')}
                                sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid #0065FF', color: '#0065FF' }}>
                                Open in new tab
                            </Button>
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ManageCards;